import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { teamsService, userService } from '../../services/api';

const TeamDelegations = ({ open, onClose, team, onUpdate }) => {
  const [users, setUsers] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (open && team) {
      initializeData();
    }
  }, [open, team]);

  const initializeData = async () => {
    try {
      // 1. Charger les utilisateurs d'abord
      await loadUsers();
      
      // 2. Ensuite enrichir les délégations
      await enrichDelegations(team.delegations || []);
    } catch (err) {
      console.error('Erreur initialisation:', err);
      setError('Erreur lors du chargement des données');
    }
  };

  const enrichDelegations = async (rawDelegations) => {
    try {
      // Récupérer les utilisateurs frais si nécessaire
      let currentUsers = users;
      if (currentUsers.length === 0) {
        const response = await userService.getAll();
        currentUsers = response.filter(user => user.role === 'ADMIN');
      }

      // Enrichir les délégations avec les infos utilisateur
      const enrichedDelegations = rawDelegations
        .filter(delegation => delegation.isActive !== false) // Filtrer les délégations actives
        .map(delegation => {
          const user = currentUsers.find(u => u.id === delegation.delegatedToUserId);
          return {
            ...delegation,
            userName: user ? `${user.firstName} ${user.lastName}` : 'Utilisateur inconnu',
            userEmail: user ? user.email : 'Email inconnu'
          };
        });

      setDelegations(enrichedDelegations);
    } catch (err) {
      console.error('Erreur enrichissement délégations:', err);
      setDelegations(rawDelegations.filter(d => d.isActive !== false)); // Fallback avec données brutes actives
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getAll();
      // Filtrer les utilisateurs qui sont ADMIN (pas SUPER_ADMIN ni VIEWER)
      const adminUsers = response.filter(user => user.role === 'ADMIN');
      setUsers(adminUsers);
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs: ' + err.message);
    }
  };

  // Fonction utilitaire pour les appels API
  const apiCall = async (url, options = {}) => {
    // Utiliser la même logique de détection d'URL que le service API centralisé
    const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
      ? 'http://localhost:3002'
      : (window.location.hostname.match(/^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\./) 
        ? `${window.location.protocol}//${window.location.hostname}:3002`
        : 'https://api.astreintes.provserver.ovh');

    const response = await fetch(`${API_BASE_URL}${url}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  const handleAddDelegation = async () => {
    if (!selectedUser) return;

    // Vérifier si l'utilisateur n'est pas déjà délégué
    if (delegations.some(d => d.userId === selectedUser)) {
      setError('Cet utilisateur est déjà délégué pour cette équipe');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await apiCall(`/api/teams/${team.id}/delegations`, {
        method: 'POST',
        body: JSON.stringify({ 
          delegatedToUserId: selectedUser,
          delegatedByUserId: null // Peut être null pour l'instant
        })
      });

      // Mettre à jour les délégations localement
      const user = users.find(u => u.id === selectedUser);
      const newDelegation = {
        userId: selectedUser,
        userEmail: user.email,
        userName: `${user.firstName} ${user.lastName}`,
        delegatedAt: new Date().toISOString()
      };
      
      const updatedDelegations = [...delegations, newDelegation];
      setDelegations(updatedDelegations);
      setSelectedUser('');
      setSuccess('Délégation ajoutée avec succès');
      
      // Notifier le parent pour mise à jour
      if (onUpdate) {
        onUpdate({ ...team, delegations: updatedDelegations });
      }
    } catch (err) {
      setError('Erreur lors de l\'ajout de la délégation: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDelegation = async (userId) => {
    setLoading(true);
    setError('');
    
    try {
      await apiCall(`/api/teams/${team.id}/delegations/${userId}`, {
        method: 'DELETE'
      });

      // Mettre à jour les délégations localement
      const updatedDelegations = delegations.filter(d => d.delegatedToUserId !== userId);
      setDelegations(updatedDelegations);
      setSuccess('Délégation supprimée avec succès');
      
      // Notifier le parent pour mise à jour
      if (onUpdate) {
        onUpdate({ ...team, delegations: updatedDelegations });
      }
    } catch (err) {
      setError('Erreur lors de la suppression de la délégation: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAdminUser = () => {
    return users.find(u => u.id === team?.adminUserId);
  };

  const getAvailableUsers = () => {
    return users.filter(u => 
      u.id !== team?.adminUserId && // Pas l'admin principal
      !delegations.some(d => d.delegatedToUserId === u.id) // Pas déjà délégué
    );
  };

  if (!team) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AdminIcon color="primary" />
          Gestion des Délégations - {team.name}
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Administrateur principal */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Administrateur Principal
          </Typography>
          {getAdminUser() ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                  {getAdminUser().firstName} {getAdminUser().lastName}
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.8 }}>
                  {getAdminUser().email}
                </Typography>
              </Box>
              <Chip 
                label="Admin Principal" 
                size="small" 
                sx={{ bgcolor: 'primary.dark', color: 'primary.contrastText' }}
              />
            </Box>
          ) : (
            <Alert severity="warning">Aucun administrateur principal défini</Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Délégations existantes */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Délégations Actuelles ({delegations.length})
          </Typography>
          
          {delegations.length === 0 ? (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
              Aucune délégation configurée
            </Typography>
          ) : (
            <List>
              {delegations.map((delegation) => (
                <ListItem key={delegation.delegatedToUserId} divider>
                  <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                    <PersonIcon />
                  </Avatar>
                  <ListItemText
                    primary={delegation.userName}
                    secondary={`${delegation.userEmail} • Délégué le ${new Date(delegation.delegatedAt).toLocaleDateString('fr-FR')}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() => handleRemoveDelegation(delegation.delegatedToUserId)}
                      color="error"
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Ajouter une nouvelle délégation */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Ajouter une Délégation
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <FormControl fullWidth>
              <InputLabel>Sélectionner un administrateur</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                disabled={loading || getAvailableUsers().length === 0}
              >
                {getAvailableUsers().map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem' }}>
                        {user.firstName.charAt(0)}
                      </Avatar>
                      {user.firstName} {user.lastName} ({user.email})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddDelegation}
              disabled={loading || !selectedUser || getAvailableUsers().length === 0}
            >
              Ajouter
            </Button>
          </Box>
          
          {getAvailableUsers().length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Aucun administrateur disponible pour délégation
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamDelegations;